import { createFileRoute, useParams } from '@tanstack/react-router';

import { HStack, Spacer, VStack } from '@/components/Layout';
import { Loader } from '@/components/Loading';
import { Text } from '@/components/Typography/Text';
import { HistoryItem, TableofContentsLeftRail } from '@/features/history';
import { stacktraceNS, useTranslation } from '@/lib/i18n';

import { useGetIssueActivities } from './-query';

export const Route = createFileRoute(
  '/_stacktrace/issues/$id/_layout/history/',
)({
  component: IssueHistoryTab,
});

/**
 * Issue 詳細 > 履歴タブ。
 *
 * BE `/v2/qa-force/issues/{id}/activities` を limit=100 で叩き、共有
 * `features/history` 配下の `HistoryItem` / `TableofContentsLeftRail` で
 * デバイス履歴と同じ UI を再利用する。
 *
 * **playbook §3.18 注意**: 過去に Loader / HistoryItem / 共有 features/history
 * を一気に import すると tsgo cumulative type cache が境界を越えて
 * TS2859 cascade を引き起こした実績がある。inline 配置のため cold typecheck
 * で再発を監視する。
 */
function IssueHistoryTab() {
  const { id } = useParams({
    from: '/_stacktrace/issues/$id/_layout/history/',
  });
  const { data, isPending } = useGetIssueActivities(id);
  const { t } = useTranslation(stacktraceNS, {
    keyPrefix: 'issues.detail.history',
  });

  if (isPending || !data) {
    return <Loader />;
  }

  if (data.histories.length === 0) {
    return (
      <VStack className="flex-1 items-center justify-center py-12">
        <Text variant="labelSemiLarge" className="text-neutral-muted-on-fill">
          {t('empty')}
        </Text>
      </VStack>
    );
  }

  return (
    <HStack className="h-full p-10 gap-8">
      <nav className="sticky top-0 h-fit w-[240px]">
        <TableofContentsLeftRail
          historiesGroupByValidFrom={data.historiesGroupByValidFrom}
          basePath={`/issues/${id}/history`}
        />
      </nav>
      <VStack className="w-full relative">
        <Spacer size={24} />
        <HistoryItem
          historiesGroupByValidFrom={data.historiesGroupByValidFrom}
        />
      </VStack>
    </HStack>
  );
}
